import React, { useContext, useEffect, useState } from "react"
import { Div, Text } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faKey } from "@fortawesome/free-solid-svg-icons"
import { motion } from "framer-motion"
import { toast } from "react-toastify"
import Card from "../atoms/Card"
import { theme } from "../settings/config"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { callFunction } from "../services/firebase.service"
import { getCoach } from "../utils/firestore.utils"
import { ROLE_LABELS } from "../utils/school.utils"
import { WideButton } from "../school/SheetBlocks"

// Un circolo che qualcuno ti passa. Il proprietario lo propone dalla scheda
// di chi lo riceve, e qui chi lo riceve dice sì o no: diventare
// proprietario è un compito — decidere chi fa cosa — e non lo si dà a
// nessuno senza che lo voglia. Sta in cima al profilo, sopra i Primi passi,
// in qualunque circolo si stia guardando, e se ne va con la risposta, o
// quando la proposta viene ritirata. Vedi "Passare il circolo" in
// docs/circoli.md.
function OwnershipOfferCard() {
	const { coach } = useContext(profileContext)
	const { schools } = useContext(schoolContext)

	const offered = Object.values(schools).find(
		school => coach?.coachID && school.ownershipOffer?.to === coach.coachID,
	)
	const offer = offered?.ownershipOffer

	// chi lo propone: tra i maestri del circolo se sono già arrivati, se no
	// lo si legge
	const [fromName, setFromName] = useState(null)
	useEffect(() => {
		setFromName(null)
		if (!offer?.from) return
		let isCurrent = true
		const known = Array.isArray(offered.coaches) ? null : offered.coaches?.[offer.from]
		if (known) {
			setFromName(known.coachName)
			return
		}
		getCoach(offer.from)
			.then(from => isCurrent && setFromName(from?.coachName ?? null))
			.catch(() => {})
		return () => {
			isCurrent = false
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [offer?.from, offered?.schoolID])

	const [isAnswering, setIsAnswering] = useState(false)

	if (!offer) return null

	const schoolName = offered.schoolName ?? "il circolo"
	const from = fromName ?? "Il proprietario"
	const fromFirst = fromName ? fromName.split(" ")[0] : "Chi te lo passa"

	const answer = async accept => {
		setIsAnswering(true)
		try {
			const answerOffer = callFunction("answerSchoolOwnership")
			await answerOffer({ schoolID: offered.schoolID, accept })
			toast.success(accept ? `${schoolName} è tuo` : "Va bene, resta com'è", {
				position: "top-center",
			})
		} catch (err) {
			toast.error(err.message || "Non sono riuscito a rispondere", {
				position: "top-center",
			})
		} finally {
			setIsAnswering(false)
		}
	}

	return (
		<Card bg={theme.colors.surface}>
			<Div d="flex" align="center" style={{ gap: "0.75rem" }}>
				<Div
					aria-hidden="true"
					w="44px"
					h="44px"
					rounded="circle"
					bg={theme.colors.primary_light}
					d="flex"
					align="center"
					justify="center"
					style={{ flexShrink: 0 }}>
					<FontAwesomeIcon icon={faKey} color={theme.colors.primary} />
				</Div>
				<Div>
					<Text textWeight="bold" textSize="subheader">
						Il circolo è tuo, se vuoi
					</Text>
					<Text textSize="caption" textColor={theme.colors.text_light}>
						{from} ti propone {schoolName}
					</Text>
				</Div>
			</Div>

			<Div bg={theme.colors.background} rounded="15px" p="0.875rem" m={{ t: "1rem" }}>
				<Text textSize="body" textColor={theme.colors.gunmetal}>
					Diventi proprietario: decidi chi fa cosa, fai entrare e togli colleghi, e puoi
					chiudere il circolo.
				</Text>
				<Text textSize="body" textColor={theme.colors.gunmetal} m={{ t: "0.4rem" }}>
					{fromFirst} resterà{" "}
					{ROLE_LABELS[offer.ownerBecomes]?.toLowerCase() ?? "nel circolo"}.
				</Text>
			</Div>

			<Div m={{ t: "1rem" }}>
				<WideButton
					text={isAnswering ? "Un attimo..." : "Accetta il circolo"}
					bg={theme.colors.primary}
					color={theme.colors.white}
					shadow={theme.shadows.accent}
					onClick={() => !isAnswering && answer(true)}
				/>
			</Div>
			<motion.button
				type="button"
				whileTap={{ scale: 0.97 }}
				disabled={isAnswering}
				onClick={() => answer(false)}
				style={{
					display: "block",
					width: "100%",
					marginTop: "0.5rem",
					padding: "0.75rem 0",
					border: 0,
					background: "transparent",
					color: theme.colors.gunmetal,
					font: "inherit",
					cursor: "pointer",
				}}>
				Non ora
			</motion.button>
		</Card>
	)
}

export default OwnershipOfferCard
